<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMainStore } from '@/stores/main'
import CountryFlag from '@/components/CountryFlag.vue'

/**
 * Track name, with the flag of its country and the link to the track page.
 *
 * The player name has UserName, the track name has this one: every page writes a track the same way
 * and gets the flag and the link for free. The country comes from the tracks of the store (loaded
 * once with the bootstrap, see store.loadBootstrap), the name from the translations.
 *
 * It is a link, so it cannot go inside another link: inside a `v-card :to` leave it out.
 */
const props = defineProps({
  /** Track id, as the server configures it (config/tracks.php) and the API returns it. */
  trackId: { type: String, default: null },
  /** Drop the flag where the name sits in a tight spot: a chip, a table header, a select. */
  flag: { type: Boolean, default: true },
})

const store = useMainStore()
const { t } = useI18n()

const country = computed(() => store.tracks.find(track => track.id === props.trackId)?.country ?? null)
const to = computed(() => ({ name: 'track', params: { trackId: props.trackId } }))
</script>

<template>
  <RouterLink v-if="trackId" :to="to" class="track-name track-name--link">
    <CountryFlag v-if="flag && country" :country="country" class="track-name__flag" />
    {{ t(`tracks.${trackId}`) }}
  </RouterLink>
  <span v-else class="track-name">–</span>
</template>

<style scoped>
/* As for the player name: the track keeps the colour of the context and only shows itself as a link
   on hover, so a list of tracks stays readable instead of becoming a wall of blue. */
.track-name {
  color: inherit;
  text-decoration: none;
}
.track-name--link:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.track-name__flag {
  margin-right: 6px;
}
</style>
